import { LEGAL_COMPANY as C } from './company';
import { DELETE_ACCOUNT_URL } from './delete-account';
import type { LegalDoc } from './types';

export const PRIVACY_POLICY: LegalDoc = {
  title: 'Privacy Policy',
  summary:
    'How FinMin collects, uses, stores, secures and shares your personal data, and the rights you have over it.',
  lastUpdated: C.lastUpdated,
  intro: [
    `This Privacy Policy explains how ${C.entity} (“FinMin”, “we”, “us” or “our”) handles personal data collected through ${C.website}, ${C.customerApp}, the provider portal and our mobile applications (together, the “Platform”), and how that data is stored, secured and shared with the parties needed to fulfil the services you request.`,
    'It is the notice we give you under Section 5 of the Digital Personal Data Protection Act, 2023 (“DPDP Act”) and the Digital Personal Data Protection Rules, 2025 (“DPDP Rules”). It is also published under Rule 4 of the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 (“SPDI Rules”) and Rule 3(1)(a) of the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021. Capitalised terms not defined here have the meaning given in our Terms of Use.',
  ],
  sections: [
    {
      id: 'scope',
      heading: 'Who we are and what this policy covers',
      blocks: [
        'For personal data collected through the Platform, FinMin is the Data Fiduciary: we decide why and how it is processed. The Official Providers who fulfil your Services, and the vendors who host or operate parts of the Platform, process it on our behalf under written contracts. To the extent professional rules require an Official Provider to keep its own records of an engagement, it does so subject to the same confidentiality and security obligations.',
        'This policy applies to customers, visitors, Official Providers and their staff, and any other person whose personal data reaches us through the Platform.',
      ],
    },
    {
      id: 'laws',
      heading: 'Laws we follow',
      blocks: [
        {
          list: [
            'Digital Personal Data Protection Act, 2023 and Digital Personal Data Protection Rules, 2025, as their provisions come into force;',
            'Information Technology Act, 2000, including Section 43A (reasonable security practices) and Section 72A (disclosure in breach of a lawful contract), the SPDI Rules for so long as they apply, and the Intermediary Guidelines and Digital Media Ethics Code Rules, 2021;',
            'Directions issued by CERT-In under Section 70B(6) of the Information Technology Act, 2000 (28 April 2022) on reporting cyber security incidents and maintaining logs;',
            'the Income-tax Act, 1961 and the Income-tax Act, 2025 as applicable, and the Income Tax Department’s Electronic Return Intermediary (ERI) scheme and guidelines;',
            'the Aadhaar (Targeted Delivery of Financial and Other Subsidies, Benefits and Services) Act, 2016; and',
            'Reserve Bank of India directions applicable to payment aggregators and digital payments.',
          ],
        },
      ],
    },
    {
      id: 'data-we-collect',
      heading: 'Personal data we collect',
      blocks: [
        { subheading: 'Data you give us' },
        {
          list: [
            'Account data: name, email address and mobile number (needed to create your account), and optionally a referral code.',
            'Identity and tax data: PAN, date of birth, address, residential status, the last digits of your Aadhaar as they appear on documents, and PANs you add for family members with their authorisation.',
            'Financial data: salary and other income, bank account details, investments, capital gains, loans, insurance policies, rent paid, deductions claimed, business information, and answers to questionnaires on the Platform. Under the SPDI Rules, financial information such as bank account details is sensitive personal data.',
            'Documents you upload, such as Form 16, rent agreements and receipts, investment proofs and statements, and the personal data of others contained in them (for example, a landlord’s name and PAN).',
            'Communications: messages exchanged with FinMin and Official Providers in in-app chat, support and grievance tickets, and records of calls or emails with us.',
          ],
        },
        { subheading: 'Data we collect with your consent from the IT Portal' },
        'Through ERI integration, with your OTP-based consent: Form 26AS, AIS and TIS, pre-filled return data, your PAN profile, earlier returns and acknowledgements, intimations, notices, and refund and demand status, as described in the Terms of Use.',
        { subheading: 'Data generated when you use the Platform' },
        {
          list: [
            'Payment data: order and transaction identifiers, amounts, status and the payment method type. Card, UPI and net-banking credentials are collected by our RBI-authorised payment aggregator, not by FinMin.',
            'Technical data: IP address, device and browser type, operating system, app version, pages visited, timestamps, and security and audit logs.',
            'Rewards and referral data: points earned and redeemed, referral codes used, and a masked view of the mobile numbers of people you referred.',
            'Derived data: Draft Computations, tax-saving analyses, and other outputs generated from the data above.',
          ],
        },
        'We do not collect biometric data. We do not ask for your IT Portal password, card PIN or UPI PIN, and you should never share them with anyone.',
      ],
    },
    {
      id: 'purposes',
      heading: 'Why we use your data',
      blocks: [
        'We process personal data only for the purposes below, which are the specified purposes for which you give consent:',
        {
          list: [
            'to create and secure your account and verify your identity through OTP;',
            'to fetch, analyse and organise your tax and financial information and prepare Draft Computations;',
            'to assign your Service to an Official Provider and let them prepare, file and follow up on it;',
            'to communicate with you about your account and Services, including service updates and reminders;',
            'to process payments, refunds, rewards and referrals, and issue invoices;',
            'to provide support and resolve grievances;',
            'to keep the Platform secure, prevent fraud and misuse, and maintain audit trails;',
            'to improve the Platform using aggregated or de-identified analytics; and',
            'to comply with Applicable Law and lawful requests from authorities, and to establish, exercise or defend legal claims.',
          ],
        },
        'We will send you promotional messages only with your consent, and you can opt out at any time. We do not sell your personal data.',
      ],
    },
    {
      id: 'consent',
      heading: 'Consent and how to withdraw it',
      blocks: [
        'We rely on your consent under Section 6 of the DPDP Act, given at sign-up and when you authorise specific actions such as ERI access, and on the legitimate uses permitted by Section 7 of the DPDP Act, such as complying with a legal obligation.',
        'You may withdraw your consent at any time, as easily as you gave it, by writing to us or using the options on the Platform, and you can revoke ERI access from the IT Portal. Withdrawal does not affect processing done before it. After withdrawal we will stop processing the data concerned, and have our Official Providers and vendors stop too, unless Applicable Law requires or permits us to retain it. Some Services cannot be provided without the data in question.',
      ],
    },
    {
      id: 'ai',
      heading: 'AI and automated processing',
      blocks: [
        'FinMin uses AI models to read documents, extract figures, prepare Draft Computations and power assistant features. We protect your data when doing so as follows:',
        {
          list: [
            'FinMin does not directly share, store or transfer your data to any third-party AI platform. Where an AI model is used, information is passed only through secure API integrations and MCP (Model Context Protocol) servers, for the sole purpose of fulfilling the Service.',
            'Only the data needed for a specific task is sent, over encrypted connections. FinMin does not use your personal data to build or sell profiles, or for advertising.',
            'AI output is not the final word. Draft Computations are reviewed by a qualified Official Provider before anything is filed, and no return is filed without your approval.',
            'You can ask for a human review of any AI-generated output that concerns you by contacting support.',
          ],
        },
      ],
    },
    {
      id: 'sharing',
      heading: 'Who we share data with',
      blocks: [
        'We share personal data only as needed for the purposes above:',
        {
          list: [
            'Official Providers: only with the Official Provider assigned to your Service, and only the information and documents needed to fulfil it.',
            'Government portals: the Income Tax Department, GST Network and other authorities, when a return, form or response is filed on your behalf.',
            'Service vendors acting on our instructions: cloud hosting and storage, authentication and in-app messaging (such as Google Firebase), email and SMS delivery, payment aggregation, analytics, AI model access, and ERI partners.',
            'Authorities and courts, where required by Applicable Law or a lawful order, or to protect the rights, safety or property of FinMin, our users or others.',
            'A successor entity in a merger, acquisition, restructuring or sale of business, which will be bound by this policy for the data transferred.',
          ],
        },
        'Every Official Provider and vendor is bound by a contract that restricts its use of personal data to our instructions and requires appropriate security and confidentiality.',
      ],
    },
    {
      id: 'providers',
      heading: 'How we vet Official Providers',
      blocks: [
        'Official Providers are thoroughly vetted before they are onboarded. We check their professional registration (for example, ICAI membership and certificate of practice, ICSI membership or Bar Council enrolment), identity and track record, so that they meet the eligibility, competence and trust needed for the Services on the Platform. They agree to confidentiality and data-protection terms, including a ban on putting customer data into public AI tools, and their access ends when their engagement ends.',
      ],
    },
    {
      id: 'security',
      heading: 'How we keep data secure',
      blocks: [
        'We acknowledge our responsibility to keep customer data secure at all times, to uphold data privacy and security norms as required by Applicable Law, and to ensure that no unauthorised person is given access to it. Our reasonable security practices, as contemplated by Section 43A of the Information Technology Act, 2000 and Section 8(5) of the DPDP Act, include:',
        {
          list: [
            'encryption of data in transit (TLS) and at rest;',
            'role-based access, so staff and Official Providers see only the data needed for their work;',
            'OTP-based login, short-lived access tokens and automatic session expiry;',
            'documents served only through authenticated requests, never through public links;',
            'audit logs of access to personal data, and monitoring for unauthorised access; and',
            'contractual security obligations on Official Providers and vendors, with periodic review.',
          ],
        },
        'No system is completely secure. Please protect your device and OTPs, and tell us immediately if you suspect any unauthorised access.',
      ],
    },
    {
      id: 'breach',
      heading: 'If a personal data breach happens',
      blocks: [
        'If a personal data breach occurs, we will contain it, investigate it, and inform the affected users and the Data Protection Board of India in the manner and within the time required by the DPDP Rules. We will also report cyber security incidents to CERT-In within 6 hours as required by its directions. Our notice to you will describe what happened, the likely consequences, what we are doing about it, and the steps you can take to protect yourself.',
      ],
    },
    {
      id: 'retention',
      heading: 'How long we keep data',
      blocks: [
        'We keep personal data only for as long as needed for the purposes for which it was collected, or as required by law:',
        {
          list: [
            'Account and profile data: while your account is active. After you deactivate it, we erase or anonymise it within 90 days, except for the records below.',
            'Tax documents, computations, filed returns and Service records: for 8 years from the end of the financial year to which they relate, so that you and the Official Provider can respond to assessments, reassessments and notices under the Income-tax Law.',
            'Payment, invoice and refund records: for the period required by tax, accounting and RBI rules, currently 8 years.',
            'Security and access logs: for at least the period required by the CERT-In directions (180 days, maintained in India) and the DPDP Rules.',
            'Grievance and dispute records: until the matter is closed and any limitation period has passed.',
          ],
        },
        'When a retention period ends we securely delete the data, or anonymise it so that it no longer identifies you, and we instruct our Official Providers and vendors to do the same.',
      ],
    },
    {
      id: 'rights',
      heading: 'Your rights',
      blocks: [
        'Under the DPDP Act you have the right to:',
        {
          list: [
            'obtain a summary of the personal data we process and the processing activities, and the identities of the Data Fiduciaries and Data Processors with whom it has been shared, with a description of the data shared (Section 11);',
            'have your personal data corrected, completed, updated or erased (Section 12), subject to the retention requirements above;',
            'withdraw your consent (Section 6(4));',
            'have your grievances redressed by us (Section 13), and, if you are not satisfied, complain to the Data Protection Board of India; and',
            'nominate another person to exercise your rights if you die or become incapable (Section 14).',
          ],
        },
        'To exercise these rights, write to our Grievance Officer at the address below from your registered email address or mobile number. We may need to verify your identity before acting. The DPDP Act also places duties on you, including providing authentic information and not impersonating anyone (Section 15).',
        {
          lead: 'To have your account and data deleted, follow the steps at',
          link: DELETE_ACCOUNT_URL,
          href: DELETE_ACCOUNT_URL,
        },
      ],
    },
    {
      id: 'cookies',
      heading: 'Cookies, storage and analytics',
      blocks: [
        'The Platform uses your browser’s storage to keep you signed in. Session tokens are kept in session storage and a refresh token in local storage, and the installed app may cache files so it loads quickly. We use Google Analytics to understand how pages are used, in aggregated form. You can clear stored data from your browser settings; doing so will sign you out.',
      ],
    },
    {
      id: 'children',
      heading: 'Children',
      blocks: [
        'The Platform is meant for people aged 18 and over. If you request a Service for a minor (for example, for income clubbed with yours), you confirm that you are the parent or lawful guardian and consent to the processing of the minor’s personal data for that Service, as required by Section 9 of the DPDP Act. We do not track minors or target advertising at them.',
      ],
    },
    {
      id: 'transfers',
      heading: 'Where data is processed',
      blocks: [
        'Personal data is stored and processed on secure servers that may be located in or outside India. Any transfer outside India is made in accordance with Section 16 of the DPDP Act and will not be made to a country or territory restricted by the Central Government.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to this policy',
      blocks: [
        'FinMin may change this policy at any time as required, while continuing to protect customer data. We will publish the updated policy on the Platform with a revised “last updated” date and, for material changes, notify you by email or in-app message, and seek fresh consent where the law requires it.',
      ],
    },
    {
      id: 'contact',
      heading: 'Grievance Officer and contact',
      blocks: [
        'For any question, request or complaint about your personal data, contact our Grievance Officer:',
        {
          list: [
            `Name: ${C.grievanceOfficer}`,
            `Email: ${C.grievanceEmail}`,
            `Address: ${C.entity}, ${C.registeredOffice}`,
          ],
        },
        'We will acknowledge your request within 24 hours and aim to resolve it within 15 days, and in any case within the period set by Applicable Law.',
      ],
    },
  ],
};
